<!--
  - This file is part of the Web3 Library developed by mFactory GmbH.
  -
  - Copyright © 2021, mFactory GmbH
  -
  - Solana Reference Stake Pool is free software: you can redistribute it
  - and/or modify it under the terms of the GNU Affero General Public License
  - as published by the Free Software Foundation, either version 3
  - of the License, or (at your option) any later version.
  -
  - Solana Reference Stake Pool is distributed in the hope that it
  - will be useful, but WITHOUT ANY WARRANTY; without even the implied
  - warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  - See the GNU Affero General Public License for more details.
  -
  - You should have received a copy of the GNU Affero General Public License
  - along with this program.
  - If not, see <https://www.gnu.org/licenses/agpl-3.0.html>.
  -
  - You can be released from the requirements of the Affero GNU General Public License
  - by purchasing a commercial license. The purchase of such a license is
  - mandatory as soon as you develop commercial activities using the
  - Solana Reference Stake Pool code without disclosing the source code of
  - your own applications.
  -
  - The developer of this program can be contacted at <info@mfactory.ch>.
  -->

<script lang="ts">
import { evaPerson } from '@quasar/extras/eva-icons'
import { computed, defineComponent, ref, watch } from 'vue'
import { useEmitter } from '~/hooks'
import {
  DELINQ_UPDATE_EVENT,
  useConnectionStore,
  useValidatorActiveStore,
  useValidatorsAllStore,
} from '~/store'
import { isoTimeDifference } from '~/utils'

export default defineComponent({
  props: {
    noIcon: {
      type: Boolean,
      default: false,
    },
    noBadges: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const validatorActiveStore = useValidatorActiveStore()
    const savedValidator = computed(() => validatorActiveStore.savedValidator)
    const validatorName = computed(() => validatorActiveStore.validatorName)
    const validatorDetails = computed(() => validatorActiveStore.validatorDetails)
    const validatorImage = computed(() => validatorActiveStore.validatorImage)
    const validatorUrl = computed(() => validatorActiveStore.validatorUrl)
    const validatorInKiwi = computed(() => validatorActiveStore.validatorInKiwi)
    const validatorDelinquent = computed(() => validatorActiveStore.validatorDelinquent)
    const validatorSVM = computed(() => validatorActiveStore.validatorSVM)
    const validatorLastVote = computed(() => validatorActiveStore.validatorLastVote)
    const validatorNotFound = computed(() => validatorActiveStore.validatorNotFound)
    const validatorFee = computed(() => validatorActiveStore.commission)

    const validatorsAllStore = useValidatorsAllStore()
    const loading = computed(() => validatorsAllStore.loading)
    const connectionStore = useConnectionStore()
    const cluster = computed(() => connectionStore.cluster)
    const isDelinquent = computed(() =>
      loading.value ? savedValidator.value?.validatorDelinquent : validatorDelinquent.value,
    )
    const lastVote = computed(() =>
      loading.value ? savedValidator.value?.validatorLastVote : validatorLastVote.value,
    )
    const delinquentTime = ref('')
    watch(
      [isDelinquent, lastVote],
      () => {
        if (isDelinquent.value && lastVote.value) {
          delinquentTime.value = isoTimeDifference(lastVote.value)
        }
      },
      { immediate: true },
    )

    const emitter = useEmitter()
    emitter.on(DELINQ_UPDATE_EVENT, () => {
      if (isDelinquent.value && lastVote.value) {
        delinquentTime.value = isoTimeDifference(lastVote.value)
      }
    })

    return {
      evaPerson,
      savedValidator,
      validatorName,
      validatorDetails,
      validatorImage,
      validatorUrl,
      validatorInKiwi,
      validatorFee,
      isDelinquent,
      validatorSVM,
      validatorLastVote,
      cluster,
      loading,
      delinquentTime,
      validatorNotFound,
    }
  },
})
</script>

<template>
  <div class="validator-name row no-wrap items-center">
    <div v-if="!noIcon" class="validator-name__logo column q-mr-md justify-center relative-position">
      <q-skeleton v-if="loading && !savedValidator" type="QAvatar" class="shadow-5" size="56px" />
      <a
        v-else
        :href="loading && savedValidator ? savedValidator.validatorUrl : validatorUrl"
        target="_blank"
      >
        <q-avatar class="shadow-1" size="56px">
          <q-img
            :src="loading && savedValidator ? savedValidator.validatorImage : validatorImage"
            spinner-size="30px"
            spinner-color="white"
          >
            <template
              v-if="
                !((loading && savedValidator && savedValidator.validatorImage) || validatorImage)
              "
              #default
            >
              <q-icon :name="evaPerson" />
            </template>
            <template #error>
              <q-icon :name="evaPerson" />
            </template>
          </q-img>
        </q-avatar>
      </a>
    </div>
    <div class="validator-name__text column justify-start relative-position">
      <div v-if="(!loading || savedValidator) && !noBadges" class="validator-name__badges row no-wrap absolute">
        <q-badge
          v-if="isDelinquent"
          class="validator-name__badges__item q-mr-sm"
          color="negative"
          text-color="text-white"
        >
          DELINQUENT {{ delinquentTime ? 'FOR' : '' }}
          {{ delinquentTime }}
          <q-tooltip class="text-body2">
            Delinquent {{ delinquentTime ? 'for' : '' }}
            {{ delinquentTime }}
          </q-tooltip>
        </q-badge>

        <q-badge
          v-if="
            loading && savedValidator ? savedValidator.validatorFee === 100 : validatorFee === 100
          "
          class="validator-row__status-badge"
          color="negative"
          outline
          text-color="text-white"
        >
          PRIVATE
        </q-badge>
      </div>
      <div v-if="!loading && validatorNotFound" class="text-red">
        Validator not found in this cluster.
      </div>
      <q-skeleton v-if="loading && savedValidator" width="250px" />
      <div v-else class="q-mt-sm">
        {{ loading && savedValidator ? savedValidator.validatorName : validatorName }}
        <q-tooltip class="text-body2">
          {{ loading && savedValidator ? savedValidator.validatorName : validatorName }}
        </q-tooltip>
      </div>
      <q-skeleton v-if="loading && !savedValidator" width="250px" class="q-mt-sm" />
      <div v-else class="validator-name__details">
        {{ loading && savedValidator ? savedValidator.validatorDetails : validatorDetails }}
        <q-tooltip class="text-body2">
          {{ loading && savedValidator ? savedValidator.validatorDetails : validatorDetails }}
        </q-tooltip>
      </div>
    </div>
  </div>
</template>
